import {
  createApi,
  fetchBaseQuery,
  retry,
  BaseQueryFn,
  FetchBaseQueryError,
  FetchArgs,
} from "@reduxjs/toolkit/query/react";
import { TAG_TYPES } from "@/services/tagTypes";
import { ENDPOINTS, APIS } from "@/app/app-config";
import type { ApiName } from "@/app/app-config";
import { computeRetryDelayMs, getMaxRetriesFor, shouldRetryError } from "@/services/apiRetryPolicy";

/**
 * Signal-aware sleep used by the retry backoff. Resolves after `delayMs`
 * or rejects immediately if `signal` aborts. Without signal wiring, an
 * aborted retry cycle would block up to CAP_MS (4s) before the next
 * iteration's `failIfAborted` check fires — defeating the whole point of
 * `api.signal` for unmount / site-switch / superseded-refetch scenarios.
 *
 * Exported for unit testing the abort path deterministically.
 */
export const waitWithAbort = (delayMs: number, signal?: AbortSignal): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const id = setTimeout(resolve, delayMs);
    if (!signal) return;
    if (signal.aborted) {
      clearTimeout(id);
      reject(new Error("Aborted"));
      return;
    }
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(id);
        reject(new Error("Aborted"));
      },
      { once: true }
    );
  });

interface AuthInfo {
  realm: string;
  domain: string;
  securityToken: string;
  /**
   * Active building code. Named `activeAccountName` on the host frame's
   * postMessage payload and renamed on the way into the slice
   * (`useAppInitialization.ts` → `activeBuildingCode: json.activeAccountName`),
   * so this is the field that actually holds it — `authInfo.activeAccountName`
   * is declared on the host-side interface but never written.
   */
  activeBuildingCode?: string;
  context?: {
    /** Amazon customerId (e.g. `A1IK3K9JQ67Y2L`) — NOT a human login. */
    userId?: string;
    /** Login in email form (e.g. `alias@amazon.com`). */
    userName?: string;
  };
}

/** Minimal state shape needed by dynamicBaseQuery — avoids circular import with store. */
interface StateWithAuthInfo {
  authInfo: AuthInfo;
}

interface CustomArgs extends FetchArgs {
  apiName: ApiName;
  method?: string;
  sesameMethod?: string; // For Sesame Service x-amz-target header
  /** Return HTTP 500 to composite query logic without retrying the same request. */
  skipHttp500Retry?: boolean;
  /**
   * Append authenticated login and active building code to
   * body.request.requester before serialization.
   */
  appendRequesterIdentity?: boolean;
}

/** Stand-in for any identity segment the auth state cannot supply. */
const UNKNOWN_REQUESTER_SEGMENT = "unknown";

interface RequestBodyWithRequester {
  request: {
    requester: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

const isRequestBodyWithRequester = (body: unknown): body is RequestBodyWithRequester => {
  if (typeof body !== "object" || body === null || !("request" in body)) return false;
  const request = (body as { request?: unknown }).request;
  return (
    typeof request === "object" &&
    request !== null &&
    "requester" in request &&
    typeof (request as { requester?: unknown }).requester === "string"
  );
};

/** Auth-derived identity appended to a requester prefix. */
interface RequesterIdentity {
  /** `authInfo.context.userName` — login in email form. */
  login?: string;
  /** `authInfo.activeBuildingCode` — the host frame's activeAccountName. */
  activeAccountName?: string;
}

/**
 * Adds authenticated login and active building code to a caller-supplied
 * requester prefix, producing `surface:::login:::activeAccountName`.
 *
 * Only the prefix (everything before the first `:::`) is retained from the
 * input, so retries and pre-enriched bodies cannot accumulate segments —
 * without that, a re-serialized retry would produce
 * `surface:::old-login:::OLD:::new-login:::NEW`.
 *
 * Segments the auth state cannot supply serialize as `unknown` rather than
 * being omitted: a fixed segment count keeps the field positionally parseable
 * downstream, where a collapsed one would silently shift the building code
 * into the login position.
 */
const appendRequesterIdentity = (body: unknown, identity: RequesterIdentity): unknown => {
  if (!isRequestBodyWithRequester(body)) {
    throw new Error("appendRequesterIdentity requires body.request.requester");
  }
  const requesterPrefix = body.request.requester.split(":::", 1)[0];
  const login = identity.login?.trim() || UNKNOWN_REQUESTER_SEGMENT;
  const activeAccountName = identity.activeAccountName?.trim() || UNKNOWN_REQUESTER_SEGMENT;
  return {
    ...body,
    request: {
      ...body.request,
      requester: `${requesterPrefix}:::${login}:::${activeAccountName}`,
    },
  };
};

const getEndpoint = (authInfo: AuthInfo, { service }: { service: string }): string =>
  `${ENDPOINTS[authInfo.realm][authInfo.domain][service]}`;

/**
 * Per-attempt request timeout, in milliseconds.
 *
 * Without this, `fetchBaseQuery` has no timeout at all and a request that
 * stalls mid-flight (TCP black-hole, idle gateway, VPN flap) never settles:
 * the promise stays pending forever, `isFetching` never clears, and every
 * consumer waiting on that query waits indefinitely. `apiRetryPolicy`
 * already lists `TIMEOUT_ERROR` as retryable — a branch that could not fire
 * until this option existed.
 *
 * ── Two properties worth knowing before changing this number ──
 *
 * 1. It is PER ATTEMPT, not per request. `retry()` wraps `dynamicBaseQuery`,
 *    so each of the (1 + `DEFAULT_MAX_RETRIES`) = 3 attempts gets its own
 *    timeout, and `TIMEOUT_ERROR` is retryable. Worst-case wall time to a
 *    terminal error is therefore ~3 × this value plus backoff (capped at
 *    `CAP_MS` = 4s each), not one × this value. That is still a bounded
 *    failure where there was previously an unbounded one.
 * 2. Cutting a slow-but-working request is a regression, so the value is
 *    deliberately generous rather than tuned. The four RYTE seed endpoints
 *    fetch a 30-hour window and are gzip-enabled precisely because their
 *    payloads are large (see `GZIP_ENABLED_APIS`), and no per-endpoint
 *    latency data exists yet. Tighten this only against measured p99s.
 */
export const REQUEST_TIMEOUT_MS = 30_000;

/** APIs that benefit from gzip compression due to large payloads. */
const GZIP_ENABLED_APIS: ReadonlySet<string> = new Set([
  APIS.GetYardGoals.name,
  APIS.FetchRyteAppointments.name,
  APIS.GetYardVisits.name,
  APIS.GetYardEvents.name,
]);

/**
 * Resolves the per-service base URL and auth headers for each request, then
 * delegates to a `fetchBaseQuery` built for that service.
 *
 * Exported for unit testing — the `fetchBaseQuery` construction happens
 * per-request inside this closure, so asserting that every request carries
 * `REQUEST_TIMEOUT_MS` requires invoking it directly. Same rationale as
 * `waitWithAbort` / `createRetryBackoff` / `createRetryCondition` below.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const dynamicBaseQuery: BaseQueryFn<CustomArgs, unknown, FetchBaseQueryError> = async (
  args,
  WebApi,
  extraOptions
) => {
  const {
    apiName,
    appendRequesterIdentity: shouldAppendRequesterIdentity,
    skipHttp500Retry: _skipHttp500Retry,
    ...fetchArgs
  } = args;
  const state = WebApi.getState() as StateWithAuthInfo;
  if (!apiName) {
    throw new Error("API name is missing");
  }
  const requestArgs = shouldAppendRequesterIdentity
    ? {
        ...fetchArgs,
        apiName,
        body: appendRequesterIdentity(fetchArgs.body, {
          login: state.authInfo.context?.userName,
          activeAccountName: state.authInfo.activeBuildingCode,
        }),
      }
    : { ...fetchArgs, apiName };

  const apiConfig = APIS[apiName];
  if (!apiConfig) {
    throw new Error(`API configuration not found for: ${apiName}`);
  }

  const { service } = apiConfig;
  const baseUrl = getEndpoint(state.authInfo, {
    service,
  });

  const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    // Bounds a stalled request. See REQUEST_TIMEOUT_MS — per attempt, and
    // TIMEOUT_ERROR is retryable, so the terminal-failure wall time is ~3×.
    timeout: REQUEST_TIMEOUT_MS,
    prepareHeaders: (headers, { getState }) => {
      const currentState = getState() as StateWithAuthInfo;
      const { authInfo } = currentState;

      // Sesame Service requires specific headers
      if (service === "SesameGateService") {
        headers.set("accept", "application/json, text/javascript, */*");
        headers.set("content-encoding", "amz-1.0");
        // Set x-amz-target if provided (for Sesame Service)
        if (args.sesameMethod) {
          headers.set("x-amz-target", args.sesameMethod);
        }
        // Token is in the body for Sesame Service, not in headers
      } else {
        // For other services, use token in header
        headers.set("token", authInfo.securityToken);
      }

      headers.set("api", apiName);
      headers.set("Method", args.method || "GET");

      // Enable gzip compression for large-payload endpoints
      if (GZIP_ENABLED_APIS.has(apiName)) {
        headers.set("Accept-Encoding", "gzip");
      }

      return headers;
    },
  });
  return rawBaseQuery(requestArgs, WebApi, extraOptions);
};

/**
 * Build the `backoff` closure passed to RTK Query's `retry()`.
 *
 * Extracted so the wiring between `computeRetryDelayMs` and
 * `waitWithAbort` is exercisable in isolation — the closure form RTK
 * expects is otherwise opaque to unit tests. `computeDelay` and `wait`
 * are injectable so tests can pin the delay deterministically and
 * observe the call sequence without using fake timers on the real
 * `setTimeout`.
 */
export const createRetryBackoff =
  (
    computeDelay: (attempt: number) => number = computeRetryDelayMs,
    wait: (delayMs: number, signal?: AbortSignal) => Promise<void> = waitWithAbort
  ) =>
  async (attempt: number, _maxRetries: number, signal?: AbortSignal): Promise<void> => {
    const delayMs = computeDelay(attempt);
    await wait(delayMs, signal);
  };

/**
 * Build the `retryCondition` closure passed to RTK Query's `retry()`.
 *
 * Extracted for the same reason as `createRetryBackoff` — the closure's
 * decision is the policy gate, and we want it covered without standing
 * up a full RTK Query store. `getMax` and `shouldRetry` are injectable
 * to keep the unit tests independent of the centralized policy table.
 *
 * RTK's `retryCondition` types `error` as the generic `BaseQueryError`.
 * Our `dynamicBaseQuery` declares it as `FetchBaseQueryError`, so the
 * cast inside is safe — `shouldRetryError` defends against other
 * shapes by checking for the `status` discriminator.
 */
export const createRetryCondition =
  (
    getMax: (apiName: ApiName | undefined) => number = getMaxRetriesFor,
    shouldRetry: (error: FetchBaseQueryError | undefined) => boolean = shouldRetryError
  ) =>
  // RTK Query types `error` as `unknown` and `args` as `unknown` on
  // `RetryConditionFunction`. We widen the parameter signature here to
  // match (rather than the narrower types of the original inline
  // closure) so the factory output is assignable to RTK's expected
  // shape. The casts inside are the same defensive narrowing the
  // original closure did.
  (error: unknown, args: unknown, extra: { attempt: number }): boolean => {
    // RTK passes the original `args` object through. Our CustomArgs
    // type is wider than RTK's FetchArgs — narrow to read apiName.
    const customArgs = args as CustomArgs;
    const baseQueryError = error as FetchBaseQueryError | undefined;
    if (customArgs.skipHttp500Retry === true && baseQueryError?.status === 500) return false;

    const apiName = customArgs.apiName;
    const maxFor = getMax(apiName);
    if (extra.attempt > maxFor) return false;
    return shouldRetry(baseQueryError);
  };

/**
 * Retry-wrapped base query. Adds transient-failure retry with jittered
 * exponential backoff on top of `dynamicBaseQuery`.
 *
 * Policy is centralized in `apiRetryPolicy.ts`:
 *   - Reads: retry on transient failures (5xx, 429, network / timeout).
 *   - Non-idempotent mutations (opted out via `API_RETRY_POLICY`): no
 *     retry — the condition short-circuits based on `getMaxRetriesFor`.
 *   - All other errors (4xx, parse, custom) fail fast.
 *
 * RTK's `RetryOptions` is a discriminated union — we use `retryCondition`
 * (expressive) rather than the simpler `maxRetries` form, because the
 * per-call attempt cap comes from the policy table, not a single global
 * number. See `getMaxRetriesFor`.
 *
 * Jittered backoff is plumbed in via the `backoff` option — RTK Query's
 * default is exponential without jitter, which would cause a thundering-
 * herd on simultaneous reconnects. See `computeRetryDelayMs` docs.
 */
const dynamicBaseQueryWithRetry: BaseQueryFn<CustomArgs, unknown, FetchBaseQueryError> = retry(
  dynamicBaseQuery,
  {
    // Signal-aware wait — aborts immediately on query cancellation
    // (unmount / site switch / superseded refetch). Without this, an
    // aborted retry cycle blocks up to CAP_MS.
    backoff: createRetryBackoff(),
    retryCondition: createRetryCondition(),
  }
);

export const api = createApi({
  reducerPath: "api",
  baseQuery: dynamicBaseQueryWithRetry,
  tagTypes: TAG_TYPES,
  endpoints: () => ({}),
  keepUnusedDataFor: 1, // to make sure that the UI always get the most updated data.
});
